import Layout from "../Layout";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  FileText,
  Download,
  TrendingUp,
  DollarSign,
  Home,
  Calendar,
  FileSpreadsheet,
  FileCheck,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Import Summary Data (removed unused constants)

// Monthly Summary Data
const monthlyKPIs = [
  {
    label: "Total Properties",
    value: "147",
    change: "+12%",
    icon: Home,
    color: "text-primary",
  },
  {
    label: "Total Investment",
    value: "$8.2M",
    change: "+8%",
    icon: DollarSign,
    color: "text-success",
  },
  {
    label: "Avg Max Bid",
    value: "$55,780",
    change: "+5%",
    icon: TrendingUp,
    color: "text-info",
  },
  {
    label: "Active Properties",
    value: "89",
    change: "+15%",
    icon: CheckCircle2,
    color: "text-warning",
  },
];

const monthlyChartData = [
  { month: "Jul", properties: 45 },
  { month: "Aug", properties: 58 },
  { month: "Sep", properties: 72 },
  { month: "Oct", properties: 95 },
  { month: "Nov", properties: 118 },
  { month: "Dec", properties: 135 },
  { month: "Jan", properties: 147 },
];

const statusPieData = [
  { name: "Active", value: 89, color: "#10b981" },
  { name: "Pending", value: 42, color: "#f59e0b" },
  { name: "Completed", value: 16, color: "#3b82f6" },
];

// Master Investment Decision Report Data
const midrData = [
  {
    id: 1,
    propertyName: "Oaks Villa",
    caseNo: "1255",
    parcelId: "100002444400",
    lienAmount: 50000,
    minBid: 55000,
    arv: 180000,
    roughARV: 175000,
    finalARV: 180000,
    rehabEstimate: 35000,
    rehabType: "Moderate",
    maxBid: 87500,
    bed: 4,
    bath: 3,
    sqFootage: 2200,
    status: "Active",
    roi: "28%",
    profitPotential: 57500,
  },
  {
    id: 2,
    propertyName: "Sunset Villa",
    caseNo: "1256",
    parcelId: "100002444401",
    lienAmount: 54350,
    minBid: 60000,
    arv: 165000,
    roughARV: 160000,
    finalARV: 165000,
    rehabEstimate: 28000,
    rehabType: "Basic",
    maxBid: 75000,
    bed: 3,
    bath: 2,
    sqFootage: 1850,
    status: "Pending",
    roi: "22%",
    profitPotential: 42650,
  },
  {
    id: 3,
    propertyName: "Pine Residence",
    caseNo: "1257",
    parcelId: "100002444402",
    lienAmount: 32000,
    minBid: 38000,
    arv: 125000,
    roughARV: 120000,
    finalARV: 125000,
    rehabEstimate: 22000,
    rehabType: "Basic",
    maxBid: 62500,
    bed: 3,
    bath: 2,
    sqFootage: 1600,
    status: "Active",
    roi: "35%",
    profitPotential: 40500,
  },
  {
    id: 4,
    propertyName: "Palm Greens",
    caseNo: "1258",
    parcelId: "100002444403",
    lienAmount: 75000,
    minBid: 82000,
    arv: 245000,
    roughARV: 240000,
    finalARV: 245000,
    rehabEstimate: 48000,
    rehabType: "Extensive",
    maxBid: 115000,
    bed: 5,
    bath: 4,
    sqFootage: 3200,
    status: "Active",
    roi: "31%",
    profitPotential: 82000,
  },
  {
    id: 5,
    propertyName: "Divine Buds",
    caseNo: "1259",
    parcelId: "100002444404",
    lienAmount: 45000,
    minBid: 50000,
    arv: 155000,
    roughARV: 150000,
    finalARV: 155000,
    rehabEstimate: 30000,
    rehabType: "Moderate",
    maxBid: 72500,
    bed: 4,
    bath: 3,
    sqFootage: 2000,
    status: "Completed",
    roi: "27%",
    profitPotential: 52500,
  },
];

const PP_Reports: React.FC = () => {
  // const [selectedMonth, setSelectedMonth] = useState("january"); // Unused - commented out
  const [selectedFormat, setSelectedFormat] = useState("pdf");
  const [activeTab, setActiveTab] = useState("import");
  const [importHistoryFilter, setImportHistoryFilter] = useState<
    "all" | "completed" | "rejected"
  >("all");
  const schemaName = "Property Master";

  // Fetch schema columns first to get all available fields
  const { data: propertyMasterColumnData } = useQuery({
    queryKey: ["columnGridData", schemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(schemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!schemaName,
    retry: false,
  });

  // Fetch Property Master records to derive report metrics
  const { data: propertyResponse, isLoading: propertiesLoading } = useQuery({
    queryKey: ["propertyMaster", schemaName, propertyMasterColumnData],
    queryFn: async () => {
      if (!propertyMasterColumnData?.grid) {
        return [];
      }
      try {
        // Get all columns from schema
        const allColumns = propertyMasterColumnData.grid.map(
          (field: any) => field.field_name
        );
        // Ensure id, date, and propertyname are included
        const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
        if (!lowerColumns.includes("id")) {
          allColumns.push("id");
        }
        if (!lowerColumns.includes("date")) {
          allColumns.push("date");
        }
        if (!lowerColumns.includes("propertyname")) {
          allColumns.push("propertyname");
        }

        const result = await DynamicModuleService.getGridCoByConfig(
          schemaName,
          1,
          10000,
          allColumns,
          undefined
        );

        // Handle different response structures
        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (err) {
        console.error("Error fetching Property Master for reports:", err);
        return [];
      }
    },
    enabled: !!schemaName && !!propertyMasterColumnData,
    retry: false,
  });

  const properties = useMemo(() => {
    if (!propertyResponse) {
      return [];
    }
    // propertyResponse is already an array from the query
    return Array.isArray(propertyResponse) ? propertyResponse : [];
  }, [propertyResponse]);

  // Fetch Max Bid schema columns
  const { data: maxBidColumnData } = useQuery({
    queryKey: ["columnGridData", "Max Bid"],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData("Max Bid");
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: true,
    retry: false,
  });

  // Fetch all Max Bid records
  const { data: maxBidData } = useQuery({
    queryKey: ["all-max-bid-data", "Max Bid", maxBidColumnData],
    queryFn: async () => {
      if (!maxBidColumnData?.grid) {
        return [];
      }
      try {
        const allColumns = maxBidColumnData.grid.map(
          (field: any) => field.field_name
        );
        if (
          !allColumns.map((col: string) => col.toLowerCase()).includes("id")
        ) {
          allColumns.push("id");
        }
        // Add propertymasterid field for linking
        const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
        if (
          !lowerColumns.includes("propertymasterid") &&
          !lowerColumns.includes("propertymaster_id")
        ) {
          allColumns.push("propertymasterid");
        }

        const result = await DynamicModuleService.getGridCoByConfig(
          "Max Bid",
          1,
          10000,
          allColumns,
          undefined
        );

        // Handle different response structures
        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (err) {
        console.error("Error fetching Max Bid data:", err);
        return [];
      }
    },
    enabled: !!maxBidColumnData,
    retry: false,
  });

  // Fetch Rehab Estimate schema columns
  const { data: rehabEstimateColumnData } = useQuery({
    queryKey: ["columnGridData", "Rehab Estimate"],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData("Rehab Estimate");
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: true,
    retry: false,
  });

  // Fetch all Rehab Estimate records
  const { data: rehabEstimateData } = useQuery({
    queryKey: [
      "all-rehab-estimate-data",
      "Rehab Estimate",
      rehabEstimateColumnData,
    ],
    queryFn: async () => {
      if (!rehabEstimateColumnData?.grid) {
        return [];
      }
      try {
        const allColumns = rehabEstimateColumnData.grid.map(
          (field: any) => field.field_name
        );
        if (
          !allColumns.map((col: string) => col.toLowerCase()).includes("id")
        ) {
          allColumns.push("id");
        }
        // Add propertymasterid field for linking
        const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
        if (
          !lowerColumns.includes("propertymasterid") &&
          !lowerColumns.includes("propertymaster_id")
        ) {
          allColumns.push("propertymasterid");
        }

        const result = await DynamicModuleService.getGridCoByConfig(
          "Rehab Estimate",
          1,
          10000,
          allColumns,
          undefined
        );

        // Handle different response structures
        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (err) {
        console.error("Error fetching Rehab Estimate data:", err);
        return [];
      }
    },
    enabled: !!rehabEstimateColumnData,
    retry: false,
  });

  // Calculate average max bid from completed Max Bid calculations
  const _avgMaxBidFromCompleted = useMemo(() => {
    if (!maxBidData || !Array.isArray(maxBidData) || maxBidData.length === 0) {
      return 0;
    }

    // Helper to get field value (case-insensitive)
    const getFieldValue = (record: any, fieldName: string) => {
      const keys = Object.keys(record ?? {});
      const key = keys.find(k => k.toLowerCase() === fieldName.toLowerCase());
      return key ? record[key] : null;
    };

    // Filter to only completed max bid calculations
    // Completed = lienamount and cash4keys are both populated (not blank)
    const completedMaxBids = maxBidData.filter((record: any) => {
      const lienamount = getFieldValue(record, "lienamount");
      const cash4keys = getFieldValue(record, "cash4keys");

      const isLiensPopulated =
        lienamount !== undefined &&
        lienamount !== null &&
        lienamount !== "" &&
        !(typeof lienamount === "string" && lienamount.trim() === "");

      const isCash4KeysPopulated =
        cash4keys !== undefined &&
        cash4keys !== null &&
        cash4keys !== "" &&
        !(typeof cash4keys === "string" && cash4keys.trim() === "");

      return isLiensPopulated && isCash4KeysPopulated;
    });

    // Calculate average maxbid from completed records
    const maxBidValues = completedMaxBids
      .map((record: any) => {
        const maxbid = getFieldValue(record, "maxbid");
        const num = Number(maxbid);
        return isNaN(num) ? 0 : num;
      })
      .filter(val => val > 0);

    if (maxBidValues.length === 0) {
      return 0;
    }

    const sum = maxBidValues.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / maxBidValues.length);
  }, [maxBidData]);
  void _avgMaxBidFromCompleted; // Intentionally unused

  // Fetch My Property schema columns
  const { data: myPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", "My Property"],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData("My Property");
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: true,
    retry: false,
  });

  // Fetch all My Property records
  const { data: myPropertyData } = useQuery({
    queryKey: ["my-property-all", "My Property", myPropertyColumnData],
    queryFn: async () => {
      if (!myPropertyColumnData?.grid) {
        return [];
      }
      try {
        const allColumns = myPropertyColumnData.grid.map(
          (field: any) => field.field_name
        );

        const result = await DynamicModuleService.getGridCoByConfig(
          "My Property",
          1,
          10000,
          allColumns,
          undefined
        );

        // Handle different response structures
        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (err) {
        console.error("Error fetching My Property data:", err);
        return [];
      }
    },
    enabled: !!myPropertyColumnData,
    retry: false,
  });

  // Count completed My Property records (those with file uploads)
  const completedMyPropertyCount = useMemo(() => {
    if (
      !myPropertyData ||
      !Array.isArray(myPropertyData) ||
      myPropertyData.length === 0
    ) {
      console.log("No My Property data available for completed count");
      return 0;
    }

    console.log("My Property data count:", myPropertyData.length);

    // Helper to check if file upload field has data
    const hasFileData = (record: any, fieldName: string): boolean => {
      // Normalize keys to lowercase
      const normalized: any = {};
      Object.keys(record ?? {}).forEach(key => {
        normalized[key.toLowerCase()] = record[key];
      });

      const value = normalized[fieldName.toLowerCase()] ?? record[fieldName];

      if (!value) {
        return false;
      }

      // Handle string (could be JSON)
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return (
              parsed.length > 0 &&
              parsed.some(
                (item: any) =>
                  item !== null && item !== undefined && item !== ""
              )
            );
          }
          return parsed !== null && parsed !== undefined && parsed !== "";
        } catch {
          return value.trim() !== "";
        }
      }

      // Handle arrays
      if (Array.isArray(value)) {
        return (
          value.length > 0 &&
          value.some(
            (item: any) => item !== null && item !== undefined && item !== ""
          )
        );
      }

      // Handle objects
      if (typeof value === "object" && value !== null) {
        return true;
      }

      return false;
    };

    // Count records with file uploads (status = "Completed")
    const completedCount = myPropertyData.filter((record: any) => {
      return hasFileData(record, "fileupload");
    }).length;

    console.log("Completed My Property count:", completedCount);
    return completedCount;
  }, [myPropertyData]);

  // Calculate Total Investment from Max Bid + Rehab Estimate schemas
  const totalInvestmentFromSchemas = useMemo(() => {
    if (!maxBidData || !rehabEstimateData) {
      return 0;
    }

    // Helper to get field value (case-insensitive)
    const getFieldValue = (record: any, fieldName: string) => {
      const keys = Object.keys(record ?? {});
      const key = keys.find(k => k.toLowerCase() === fieldName.toLowerCase());
      return key ? record[key] : null;
    };

    // Create maps: propertymasterid -> maxbid and propertymasterid -> rehabestimate
    const maxBidMap = new Map<string, number>();
    const rehabEstimateMap = new Map<string, number>();

    // Process Max Bid records
    if (Array.isArray(maxBidData)) {
      maxBidData.forEach((record: any) => {
        const propertyId =
          getFieldValue(record, "propertymasterid") ??
          getFieldValue(record, "propertymaster_id") ??
          getFieldValue(record, "propertymasterid");
        const maxbid = getFieldValue(record, "maxbid");
        const maxbidNum = Number(maxbid);
        if (propertyId && !isNaN(maxbidNum) && maxbidNum > 0) {
          maxBidMap.set(String(propertyId), maxbidNum);
        }
      });
    }

    // Process Rehab Estimate records
    if (Array.isArray(rehabEstimateData)) {
      rehabEstimateData.forEach((record: any) => {
        const propertyId =
          getFieldValue(record, "propertymasterid") ??
          getFieldValue(record, "propertymaster_id") ??
          getFieldValue(record, "propertymasterid");
        const rehabestimate = getFieldValue(record, "rehabestimate");
        const rehabNum = Number(rehabestimate);
        if (propertyId && !isNaN(rehabNum) && rehabNum > 0) {
          rehabEstimateMap.set(String(propertyId), rehabNum);
        }
      });
    }

    // Calculate total investment: sum of (maxbid + rehabestimate) for all properties
    let total = 0;
    const allPropertyIds = new Set([
      ...Array.from(maxBidMap.keys()),
      ...Array.from(rehabEstimateMap.keys()),
    ]);

    allPropertyIds.forEach(propertyId => {
      const mb = maxBidMap.get(propertyId) ?? 0;
      const re = rehabEstimateMap.get(propertyId) ?? 0;
      total += mb + re;
    });

    console.log(
      "Total Investment calculated:",
      total,
      "from",
      allPropertyIds.size,
      "properties"
    );
    return total;
  }, [maxBidData, rehabEstimateData]);

  // Get imported property IDs from localStorage (frontend-only tracking)
  const importedPropertyIds = useMemo(() => {
    try {
      const stored = localStorage.getItem("importedPropertyIds");
      if (stored) {
        const ids = JSON.parse(stored);
        return new Set(Array.isArray(ids) ? ids : []);
      }
    } catch (error) {
      console.error(
        "Error reading imported property IDs from localStorage:",
        error
      );
    }
    return new Set<string>();
  }, []);

  // Get import history from localStorage
  const importHistory = useMemo(() => {
    try {
      const stored = localStorage.getItem("importHistory");
      if (stored) {
        const history = JSON.parse(stored);
        return Array.isArray(history) ? history : [];
      }
    } catch (error) {
      console.error("Error reading import history from localStorage:", error);
    }
    return [];
  }, []);

  // Derived report values (shadow top-level static constants)
  const {
    importSummaryData,
    importDetails,
    monthlyKPIs: computedMonthlyKPIs,
    monthlyChartData: computedMonthlyChartData,
    statusPieData: computedStatusPieData,
  } = useMemo(() => {
    const totalProperties = properties.length;
    const totalInvestment = totalInvestmentFromSchemas; // Use calculated value from schemas
    let avgMaxBid = 0;
    let maxBidCount = 0;
    let activeCount = 0;
    // let totalUploads = 0; // Unused - commented out
    let propertiesWithUploads = 0;
    const byMonth: Record<string, number> = {};

    const statusCounts: Record<string, number> = {};

    properties.forEach((p: any) => {
      const norm: Record<string, any> = {};
      Object.keys(p ?? {}).forEach(k => (norm[k.toLowerCase()] = p[k]));

      // Check if this property is imported (from localStorage)
      const propertyId = String(p.id ?? p.ID ?? p.Id ?? p._id ?? "");
      if (propertyId && importedPropertyIds.has(propertyId)) {
        propertiesWithUploads += 1;
      }

      // Note: totalInvestment is now calculated from Max Bid and Rehab Estimate schemas above
      // Keep maxbid check for avgMaxBid calculation below
      const mbValue = norm.maxbid ?? norm.max_bid ?? 0;
      const mb = isNaN(Number(mbValue)) ? 0 : Number(mbValue);

      if (mb > 0) {
        avgMaxBid += mb;
        maxBidCount += 1;
      }

      // Count all statuses properly
      const status = (norm.status ?? "").toString().trim();
      if (status) {
        const statusKey =
          status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        statusCounts[statusKey] = (statusCounts[statusKey] ?? 0) + 1;
        if (status.toLowerCase() === "active") {
          activeCount += 1;
        }
      } else {
        statusCounts["Unknown"] = (statusCounts["Unknown"] ?? 0) + 1;
      }

      // count uploads heuristically (commented out - not currently used)
      // let _uploadsForRecord = 0;
      // Object.keys(norm).forEach(key => {
      //   if (
      //     key.includes("photo") ||
      //     key.includes("file") ||
      //     key.includes("upload") ||
      //     key.includes("ua7533")
      //   ) {
      //     const val = norm[key];
      //     if (Array.isArray(val)) {
      //       _uploadsForRecord += val.length;
      //     } else if (typeof val === "string" && val.trim()) {
      //       _uploadsForRecord += 1;
      //     } else if (val && typeof val === "object") {
      //       _uploadsForRecord += 1;
      //     }
      //   }
      // });
      // totalUploads += _uploadsForRecord; // Unused - commented out

      const dateVal =
        norm.date ?? norm.created_at ?? norm.createdat ?? norm.date_created;
      if (dateVal) {
        const d = new Date(dateVal);
        if (!isNaN(d.getTime())) {
          const month = d.toLocaleString(undefined, { month: "short" });
          byMonth[month] = (byMonth[month] ?? 0) + 1;
        }
      }
    });

    const _avgMaxBidFinal =
      maxBidCount > 0 ? Math.round(avgMaxBid / maxBidCount) : 0;
    void _avgMaxBidFinal; // Intentionally unused

    // Sort monthly chart data by date (get last 7 months)
    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthlyChart = Object.keys(byMonth)
      .map(m => ({
        month: m,
        properties: byMonth[m],
        order: monthOrder.indexOf(m) !== -1 ? monthOrder.indexOf(m) : 99,
      }))
      .sort((a, b) => a.order - b.order)
      .slice(-7) // Get last 7 months
      .map(({ month, properties }) => ({ month, properties }));

    // Create status pie data - only Pending and Completed (no Active)
    // Use My Property data to determine status based on file uploads
    // Use the EXACT same logic as My Property component
    const statusColors: Record<string, string> = {
      Pending: "#f59e0b",
      Completed: "#10b981",
    };

    // Use the exact same hasFileData logic from My Property component
    const hasFileData = (record: any, fieldName: string): boolean => {
      // Normalize keys to lowercase (same as My Property)
      const normalized: any = {};
      Object.keys(record ?? {}).forEach(key => {
        normalized[key.toLowerCase()] = record[key];
      });

      const value =
        normalized[fieldName.toLowerCase()] ??
        record[fieldName] ??
        record[fieldName.toUpperCase()];

      if (!value) {
        return false;
      }

      // Handle string (could be JSON) - same logic as My Property
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return (
              parsed.length > 0 &&
              parsed.some(
                (item: any) =>
                  item !== null && item !== undefined && item !== ""
              )
            );
          }
          return parsed !== null && parsed !== undefined && parsed !== "";
        } catch {
          return value.trim() !== "";
        }
      }

      // Handle arrays - same logic as My Property
      if (Array.isArray(value)) {
        return (
          value.length > 0 &&
          value.some(
            (item: any) => item !== null && item !== undefined && item !== ""
          )
        );
      }

      // Handle objects - same logic as My Property
      if (typeof value === "object" && value !== null) {
        return true;
      }

      return false;
    };

    // Count Pending and Completed based on My Property file uploads
    // Use the same logic as My Property component's propertyFileUploadStatus
    // Create a map of propertymasterid -> hasFiles (same as My Property component)
    const propertyFileUploadStatusMap = new Map<string, boolean>();

    if (myPropertyData && Array.isArray(myPropertyData)) {
      myPropertyData.forEach((record: any) => {
        // Normalize keys to lowercase (same as My Property)
        const normalized: any = {};
        Object.keys(record ?? {}).forEach(key => {
          normalized[key.toLowerCase()] = record[key];
        });

        // Get propertymasterid (same as My Property component)
        const propertyId =
          normalized["propertymasterid"] ??
          normalized["propertymaster_id"] ??
          record["propertymasterid"] ??
          record["propertymaster_id"] ??
          null;

        if (!propertyId) {
          return;
        }

        // Check if the single file upload field has at least one file
        // Use the exact same logic as My Property component
        const hasAnyFile = hasFileData(record, "fileupload");

        propertyFileUploadStatusMap.set(String(propertyId), hasAnyFile);
      });
    }

    // Now count all properties (from Property Master)
    // Properties without My Property records are Pending
    // Properties with My Property records but no files are Pending
    // Properties with My Property records and files are Completed
    // Use the same logic as My Property component - match by propertymasterid
    let pendingCount = 0;
    let completedCount = 0;

    if (properties && Array.isArray(properties)) {
      properties.forEach((property: any) => {
        // Get property ID - normalize to handle different ID field names
        // In Property Master, the ID field is typically "id"
        const propertyId = String(
          property.id ?? property.ID ?? property.Id ?? property._id ?? ""
        );

        if (!propertyId || propertyId === "") {
          return;
        }

        // Check if this property has files (same logic as My Property component)
        // The map is keyed by propertymasterid from My Property records
        // which should match the Property Master id
        const hasFiles = propertyFileUploadStatusMap.get(propertyId) ?? false;

        if (hasFiles) {
          completedCount++;
        } else {
          pendingCount++;
        }
      });
    }

    const statusPie = [
      { name: "Pending", value: pendingCount, color: statusColors.Pending },
      {
        name: "Completed",
        value: completedCount,
        color: statusColors.Completed,
      },
    ];

    // Get the most recent import date from import history
    // Import history is stored with most recent first (unshift)
    const lastImportDate =
      importHistory.length > 0 && importHistory[0]?.date
        ? importHistory[0].date
        : properties.length > 0
          ? (properties[0].date ?? properties[0].created_at ?? "")
          : "";

    const importSummary = {
      totalImports: propertiesWithUploads,
      lastImport: lastImportDate,
      totalRows: totalProperties,
      successfulRows: totalProperties,
      errorRows: 0,
      mappingUsed: "Dynamic Import",
    };

    return {
      importSummaryData: importSummary,
      importDetails: importHistory.map(item => ({
        date: item.date,
        fileName: item.fileName,
        rows: item.rows,
        success: item.success,
        errors: item.errors,
        status: item.status,
      })),
      monthlyKPIs: [
        {
          label: "Total Properties",
          value: String(totalProperties),
          change: "",
          icon: Home,
          color: "text-primary",
        },
        {
          label: "Total Investment",
          value:
            totalInvestment >= 1000000
              ? `$${(totalInvestment / 1000000).toFixed(1)}M`
              : `$${(totalInvestment / 1000).toFixed(0)}K`,
          change: "",
          icon: DollarSign,
          color: "text-success",
        },
        {
          label: "Active Properties",
          value: String(
            completedMyPropertyCount > 0
              ? completedMyPropertyCount
              : activeCount
          ),
          change: "",
          icon: CheckCircle2,
          color: "text-warning",
        },
      ],
      monthlyChartData:
        monthlyChart.length > 0
          ? monthlyChart
          : [{ month: "Jan", properties: totalProperties }],
      statusPieData: statusPie,
    };
  }, [
    properties,
    completedMyPropertyCount,
    totalInvestmentFromSchemas,
    myPropertyData,
    importedPropertyIds,
    importHistory,
  ]);

  // Create maps for Max Bid and Rehab Estimate data linked by propertymasterid
  const maxBidMap = useMemo(() => {
    const map = new Map<string, any>();
    if (maxBidData && Array.isArray(maxBidData)) {
      maxBidData.forEach((record: any) => {
        const normalized: any = {};
        Object.keys(record ?? {}).forEach(key => {
          normalized[key.toLowerCase()] = record[key];
        });
        const propertyId =
          normalized.propertymasterid ??
          normalized.propertymaster_id ??
          record.propertymasterid ??
          record.propertymaster_id;
        if (propertyId) {
          map.set(String(propertyId), record);
        }
      });
    }
    return map;
  }, [maxBidData]);

  const rehabEstimateMap = useMemo(() => {
    const map = new Map<string, any>();
    if (rehabEstimateData && Array.isArray(rehabEstimateData)) {
      rehabEstimateData.forEach((record: any) => {
        const normalized: any = {};
        Object.keys(record ?? {}).forEach(key => {
          normalized[key.toLowerCase()] = record[key];
        });
        const propertyId =
          normalized.propertymasterid ??
          normalized.propertymaster_id ??
          record.propertymasterid ??
          record.propertymaster_id;
        if (propertyId) {
          map.set(String(propertyId), record);
        }
      });
    }
    return map;
  }, [rehabEstimateData]);

  // Helper to get field value (case-insensitive)
  const getFieldValue = (record: any, fieldName: string): any => {
    if (!record) {
      return null;
    }
    const keys = Object.keys(record);
    const key = keys.find(k => k.toLowerCase() === fieldName.toLowerCase());
    return key ? record[key] : null;
  };

  // Generate MIDR report data from actual properties - include ALL Property Master fields
  const computedMidrData = useMemo(() => {
    return properties.map((p: any, index: number) => {
      const norm: Record<string, any> = {};
      Object.keys(p ?? {}).forEach(k => (norm[k.toLowerCase()] = p[k]));

      const id = norm.id ?? index + 1;
      const propertyId = String(id);

      // Get linked Max Bid and Rehab Estimate data
      const maxBidRecord = maxBidMap.get(propertyId);
      const rehabEstimateRecord = rehabEstimateMap.get(propertyId);

      // Extract values from Property Master
      const propertyName =
        norm.propertyname ?? getFieldValue(p, "propertyname") ?? "N/A";
      const caseNo = norm.caseno ?? getFieldValue(p, "caseno") ?? "N/A";
      const parcelId = norm.parcelid ?? getFieldValue(p, "parcelid") ?? "N/A";
      const lienAmount = Number(
        norm.lienamount ?? getFieldValue(p, "lienamount") ?? 0
      );
      const minBid = Number(
        norm.minbid ??
          norm.startingbid ??
          getFieldValue(p, "minbid") ??
          getFieldValue(p, "startingbid") ??
          0
      );
      const roughARV = Number(
        norm.rougharv ?? getFieldValue(p, "rougharv") ?? 0
      );
      const finalARV = Number(
        norm.finalarv ?? getFieldValue(p, "finalarv") ?? 0
      );
      const bed = norm.bed ?? getFieldValue(p, "bed") ?? 0;
      const bath = norm.bath ?? getFieldValue(p, "bath") ?? 0;
      const sqFootage = Number(
        norm.sqfootage ?? getFieldValue(p, "sqfootage") ?? 0
      );
      const status = norm.status ?? getFieldValue(p, "status") ?? "Unknown";

      // Get Max Bid from Max Bid schema (preferred) or Property Master (fallback)
      const maxBid = maxBidRecord
        ? Number(getFieldValue(maxBidRecord, "maxbid") ?? 0)
        : Number(norm.maxbid ?? getFieldValue(p, "maxbid") ?? 0);

      // Get Rehab Estimate and Rehab Type from Rehab Estimate schema (preferred) or Property Master (fallback)
      const rehabEstimate = rehabEstimateRecord
        ? Number(getFieldValue(rehabEstimateRecord, "rehabestimate") ?? 0)
        : Number(norm.rehabestimate ?? getFieldValue(p, "rehabestimate") ?? 0);

      const rehabType = rehabEstimateRecord
        ? (getFieldValue(rehabEstimateRecord, "rehabtype") ??
          norm.rehabtype ??
          getFieldValue(p, "rehabtype") ??
          "N/A")
        : (norm.rehabtype ?? getFieldValue(p, "rehabtype") ?? "N/A");

      // Extract additional fields for Profit Spread calculation
      // Liens (from Property Master or Max Bid record)
      const liens = maxBidRecord
        ? Number(getFieldValue(maxBidRecord, "liens") ?? 0)
        : Number(
            norm.liens ??
              norm.lienamount ??
              getFieldValue(p, "liens") ??
              getFieldValue(p, "lienamount") ??
              0
          );

      // Cash for Keys (cash4keys, cash_for_keys, cashforkeys)
      const cashForKeys = maxBidRecord
        ? Number(
            getFieldValue(maxBidRecord, "cash4keys") ??
              getFieldValue(maxBidRecord, "cash_for_keys") ??
              getFieldValue(maxBidRecord, "cashforkeys") ??
              0
          )
        : Number(
            norm.cash4keys ??
              norm.cash_for_keys ??
              norm.cashforkeys ??
              getFieldValue(p, "cash4keys") ??
              getFieldValue(p, "cash_for_keys") ??
              getFieldValue(p, "cashforkeys") ??
              0
          );

      // Taxes & Services (tts, taxes_services, taxesservices)
      const taxesServices = maxBidRecord
        ? Number(
            getFieldValue(maxBidRecord, "tts") ??
              getFieldValue(maxBidRecord, "taxes_services") ??
              getFieldValue(maxBidRecord, "taxesservices") ??
              0
          )
        : Number(
            norm.tts ??
              norm.taxes_services ??
              norm.taxesservices ??
              getFieldValue(p, "tts") ??
              getFieldValue(p, "taxes_services") ??
              getFieldValue(p, "taxesservices") ??
              0
          );

      // Calculate Profit Spread = ARV - (max_bid + total_rehab + liens + cash_for_keys + taxes_services)
      const totalRehab = rehabEstimate; // total_rehab is the rehab estimate
      const profitSpread =
        finalARV - (maxBid + totalRehab + liens + cashForKeys + taxesServices);

      // Calculate ROI = (spread / max_bid) * 100 if max_bid > 0
      const roi = maxBid > 0 ? Math.round((profitSpread / maxBid) * 100) : 0;

      // Total Investment = max_bid + total_rehab + liens + cash_for_keys + taxes_services
      const totalInvestment =
        maxBid + totalRehab + liens + cashForKeys + taxesServices;

      // Return all Property Master fields plus calculated fields
      return {
        // Include all original property fields
        ...p,
        // Override with normalized values for consistency
        id,
        propertyName,
        caseNo,
        parcelId,
        lienAmount,
        minBid,
        roughARV,
        finalARV,
        rehabEstimate,
        rehabType,
        maxBid,
        bed,
        bath,
        sqFootage,
        status,
        // Calculated fields
        arv: finalARV,
        roi: `${roi}%`,
        profitSpread: Math.max(0, profitSpread),
        profitPotential: Math.max(0, profitSpread), // Keep for backward compatibility
        totalRehab,
        liens,
        cashForKeys,
        taxesServices,
        totalInvestment,
      };
    });
  }, [properties, maxBidMap, rehabEstimateMap]);

  // Get dynamic columns from Property Master schema
  const midrTableColumns = useMemo(() => {
    if (!propertyMasterColumnData?.grid) {
      return [];
    }

    // System/metadata fields to exclude from display
    const excludedFields = [
      "id",
      "created_at",
      "updated_at",
      "created_on",
      "updated_on",
      "created_by",
      "updated_by",
      "is_active",
    ];

    // Get all columns from schema
    const columns: Array<{ key: string; label: string }> =
      propertyMasterColumnData.grid
        .filter((field: any) => {
          const fieldName = field.field_name?.toLowerCase() ?? "";
          return !excludedFields.some(
            excluded => excluded.toLowerCase() === fieldName
          );
        })
        .map((field: any) => ({
          key: field.field_name,
          label:
            field.field_title ??
            field.field_name
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str: string) => str.toUpperCase())
              .trim(),
        }));

    // Add calculated fields at the end
    columns.push(
      { key: "roi", label: "ROI" },
      { key: "profitPotential", label: "Profit Potential" }
    );

    return columns;
  }, [propertyMasterColumnData]);

  // Helper to get field value from property (case-insensitive, checks multiple field name variations)
  const getPropertyValue = (property: any, fieldName: string): number => {
    // Try direct access first
    if (property[fieldName] !== undefined && property[fieldName] !== null) {
      const num = Number(property[fieldName]);
      return isNaN(num) ? 0 : num;
    }

    // Try lowercase
    const lowerField = fieldName.toLowerCase();
    if (property[lowerField] !== undefined && property[lowerField] !== null) {
      const num = Number(property[lowerField]);
      return isNaN(num) ? 0 : num;
    }

    // Try camelCase variations
    const camelCase = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    if (property[camelCase] !== undefined && property[camelCase] !== null) {
      const num = Number(property[camelCase]);
      return isNaN(num) ? 0 : num;
    }

    // Try with underscore
    const withUnderscore = fieldName.replace(/([A-Z])/g, "_$1").toLowerCase();
    if (
      property[withUnderscore] !== undefined &&
      property[withUnderscore] !== null
    ) {
      const num = Number(property[withUnderscore]);
      return isNaN(num) ? 0 : num;
    }

    // Search through all keys case-insensitively
    const keys = Object.keys(property ?? {});
    const matchingKey = keys.find(k => k.toLowerCase() === lowerField);
    if (
      matchingKey &&
      property[matchingKey] !== undefined &&
      property[matchingKey] !== null
    ) {
      const num = Number(property[matchingKey]);
      return isNaN(num) ? 0 : num;
    }

    return 0;
  };

  // Calculate summary metrics from computedMidrData
  const midrSummaryMetrics = useMemo(() => {
    if (!computedMidrData || computedMidrData.length === 0) {
      return {
        averageROI: 0,
        totalProfitPotential: 0,
        totalProfitSpread: 0,
        totalInvestment: 0,
        averageInvestment: 0,
      };
    }

    let totalROI = 0;
    let totalProfitPotential = 0;
    let totalInvestment = 0;
    let validROICount = 0;

    computedMidrData.forEach((property: any, index: number) => {
      // Extract maxBid and rehabEstimate using helper
      const maxBid = getPropertyValue(property, "maxBid");
      const rehabEstimate = getPropertyValue(property, "rehabEstimate");
      const finalARV =
        getPropertyValue(property, "finalARV") ||
        getPropertyValue(property, "arv");
      const investment = maxBid + rehabEstimate;

      // Extract ROI - handle both string format "5%" and number format
      let roiValue = 0;
      if (property.roi) {
        if (typeof property.roi === "string") {
          const parsed = parseFloat(property.roi.replace("%", "").trim());
          roiValue = isNaN(parsed) ? 0 : parsed;
        } else {
          const num = Number(property.roi);
          roiValue = isNaN(num) ? 0 : num;
        }
      } else if (investment > 0 && finalARV > 0) {
        // Calculate ROI if not present: ((Final ARV - (Max Bid + Rehab Estimate)) / (Max Bid + Rehab Estimate)) * 100
        roiValue = Math.round(((finalARV - investment) / investment) * 100);
      }

      // Only count ROI if investment > 0 (valid calculation)
      if (investment > 0) {
        totalROI += roiValue;
        validROICount++;
      }

      // Sum profit spread/profit potential - use calculated value or extract from property
      let profitSpread =
        getPropertyValue(property, "profitSpread") ||
        getPropertyValue(property, "profitPotential");
      if (profitSpread === 0 && finalARV > 0 && investment > 0) {
        // Recalculate if not present: ARV - (max_bid + total_rehab + liens + cash_for_keys + taxes_services)
        const maxBid = getPropertyValue(property, "maxBid");
        const totalRehab =
          getPropertyValue(property, "totalRehab") ||
          getPropertyValue(property, "rehabEstimate");
        const liens = getPropertyValue(property, "liens");
        const cashForKeys = getPropertyValue(property, "cashForKeys");
        const taxesServices = getPropertyValue(property, "taxesServices");
        const totalCosts =
          maxBid + totalRehab + liens + cashForKeys + taxesServices;
        profitSpread = Math.max(0, finalARV - totalCosts);
      }
      totalProfitPotential += profitSpread;

      // Sum investment - use totalInvestment if available, otherwise calculate
      let investmentValue = getPropertyValue(property, "totalInvestment");
      if (investmentValue === 0) {
        // Calculate: max_bid + total_rehab + liens + cash_for_keys + taxes_services
        const maxBid = getPropertyValue(property, "maxBid");
        const totalRehab =
          getPropertyValue(property, "totalRehab") ||
          getPropertyValue(property, "rehabEstimate");
        const liens = getPropertyValue(property, "liens");
        const cashForKeys = getPropertyValue(property, "cashForKeys");
        const taxesServices = getPropertyValue(property, "taxesServices");
        investmentValue =
          maxBid + totalRehab + liens + cashForKeys + taxesServices;
      }
      totalInvestment += investmentValue;

      // Debug logging for first few properties
      if (index < 3) {
        const propertyName =
          getPropertyValue(property, "propertyname") || `Property ${index + 1}`;
        const totalRehab =
          getPropertyValue(property, "totalRehab") ||
          getPropertyValue(property, "rehabEstimate");
        const liens = getPropertyValue(property, "liens");
        const cashForKeys = getPropertyValue(property, "cashForKeys");
        const taxesServices = getPropertyValue(property, "taxesServices");
        const totalCosts =
          maxBid + totalRehab + liens + cashForKeys + taxesServices;
        console.log(`[Profit Calculation Debug] ${propertyName}:`, {
          finalARV,
          maxBid,
          totalRehab,
          liens,
          cashForKeys,
          taxesServices,
          totalCosts,
          profitSpread,
          investmentValue,
        });
      }
    });

    const validInvestmentCount = computedMidrData.filter((p: any) => {
      const inv = getPropertyValue(p, "totalInvestment");
      return inv > 0;
    }).length;

    const summary = {
      averageROI: validROICount > 0 ? Math.round(totalROI / validROICount) : 0,
      totalProfitPotential,
      totalProfitSpread: totalProfitPotential, // Same value, different name
      totalInvestment,
      averageInvestment:
        validInvestmentCount > 0
          ? Math.round(totalInvestment / validInvestmentCount)
          : 0,
    };

    // Debug summary
    console.log("[Profit Summary Metrics]:", {
      totalProperties: computedMidrData.length,
      validInvestmentCount,
      totalProfitPotential: `$${(totalProfitPotential / 1000).toFixed(0)}K`,
      totalInvestment: `$${(totalInvestment / 1000000).toFixed(1)}M`,
      averageInvestment: `$${(summary.averageInvestment / 1000000).toFixed(1)}M`,
    });

    return summary;
  }, [computedMidrData]);

  // Use computed values, fallback to static if no data
  const finalMonthlyKPIs = computedMonthlyKPIs || monthlyKPIs;
  const finalMonthlyChartData = computedMonthlyChartData || monthlyChartData;
  const finalStatusPieData = computedStatusPieData || statusPieData;
  const finalMidrData =
    computedMidrData.length > 0 ? computedMidrData : midrData;

  // Helper to get field value (case-insensitive)
  const getFieldValueForExport = (record: any, fieldName: string) => {
    if (!record) {
      return "";
    }
    const keys = Object.keys(record ?? {});
    const normalized = fieldName.toLowerCase();
    const key = keys.find(k => {
      const clean = k.toLowerCase().replace(/[\s\-_]/g, "");
      return clean === normalized;
    });
    return key ? record[key] : "";
  };

  // Format date for export
  const formatDateForExport = (dateStr: string) => {
    if (!dateStr) {
      return "";
    }
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      return date.toISOString().split("T")[0];
    } catch {
      return dateStr;
    }
  };

  const handleExport = (reportType: string, format: string) => {
    try {
      let data: any[] = [];
      let fileName = "";

      if (activeTab === "import") {
        // Export Import History table (file imports, not individual properties)
        data = importDetails.map((item: any) => {
          // Format date for export
          const formattedDate = item.date ? formatDateForExport(item.date) : "";

          return {
            Date: formattedDate,
            "File Name": item.fileName ?? "",
            "Total Rows": item.rows ?? 0,
            Success: item.success ?? 0,
            Errors: item.errors ?? 0,
            Status: item.status ?? "",
          };
        });
        fileName = `Import_History_${new Date().toISOString().split("T")[0]}`;
      } else if (activeTab === "monthly") {
        // Export Monthly Summary KPIs
        data = finalMonthlyKPIs.map(kpi => ({
          Metric: kpi.label,
          Value: kpi.value,
        }));
        fileName = `Monthly_Summary_${new Date().toISOString().split("T")[0]}`;
      } else if (activeTab === "midr") {
        // Export MIDR Report with specific columns
        data = finalMidrData.map((property: any) => {
          const normalized: any = {};
          Object.keys(property ?? {}).forEach(k => {
            normalized[k.toLowerCase()] = property[k];
          });

          const caseNo =
            normalized.caseno ??
            getFieldValueForExport(property, "caseno") ??
            "";
          const parcelId =
            normalized.parcelid ??
            getFieldValueForExport(property, "parcelid") ??
            "";
          const date =
            normalized.date ?? getFieldValueForExport(property, "date") ?? "";
          const lienAmount =
            normalized.lienamount ??
            getFieldValueForExport(property, "lienamount") ??
            0;
          const bed =
            normalized.bed ?? getFieldValueForExport(property, "bed") ?? "";
          const bath =
            normalized.bath ?? getFieldValueForExport(property, "bath") ?? "";
          const sqFootage =
            normalized.sqfootage ??
            getFieldValueForExport(property, "sqfootage") ??
            "";
          const finalArv =
            normalized.finalarv ??
            getFieldValueForExport(property, "finalarv") ??
            0;
          const roi = property.roi ?? "0%";
          const profitPotential =
            property.profitPotential ?? property.profitSpread ?? 0;

          return {
            "Case No": caseNo,
            "Parcel ID (APN etc)": parcelId,
            Date: date ? formatDateForExport(date) : "",
            "Lien Amount":
              typeof lienAmount === "number" ? lienAmount : lienAmount,
            Bed: bed,
            Bath: bath,
            "Sq. Footage":
              typeof sqFootage === "number" ? sqFootage : sqFootage,
            "Final ARV":
              finalArv && finalArv !== 0
                ? typeof finalArv === "number"
                  ? finalArv
                  : finalArv
                : "--",
            ROI: typeof roi === "string" ? roi : `${roi}%`,
            "Profit Potential":
              typeof profitPotential === "number"
                ? profitPotential
                : profitPotential,
          };
        });

        // Add summary rows
        data.push({});
        data.push({
          "Case No": "Total Properties",
          "Parcel ID (APN etc)": finalMidrData.length,
        });
        data.push({
          "Case No": "Total Profit Potential",
          "Parcel ID (APN etc)":
            midrSummaryMetrics.totalProfitPotential >= 1000000
              ? `$${(midrSummaryMetrics.totalProfitPotential / 1000000).toFixed(1)}M`
              : `$${(midrSummaryMetrics.totalProfitPotential / 1000).toFixed(0)}K`,
        });
        data.push({
          "Case No": "Total Investment",
          "Parcel ID (APN etc)":
            midrSummaryMetrics.totalInvestment >= 1000000
              ? `$${(midrSummaryMetrics.totalInvestment / 1000000).toFixed(1)}M`
              : `$${(midrSummaryMetrics.totalInvestment / 1000).toFixed(0)}K`,
        });
        data.push({
          "Case No": "Average Investment",
          "Parcel ID (APN etc)":
            midrSummaryMetrics.averageInvestment >= 1000000
              ? `$${(midrSummaryMetrics.averageInvestment / 1000000).toFixed(1)}M`
              : `$${(midrSummaryMetrics.averageInvestment / 1000).toFixed(0)}K`,
        });

        fileName = `MIDR_Report_${new Date().toISOString().split("T")[0]}`;
      }

      if (data.length === 0) {
        toast.error("No data to export");
        return;
      }

      if (format === "csv") {
        // Convert to CSV
        const worksheet = XLSX.utils.json_to_sheet(data);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        link.click();
        toast.success(`Successfully exported ${reportType} as CSV`);
      } else if (format === "excel") {
        // Convert to Excel
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
        toast.success(`Successfully exported ${reportType} as Excel`);
      } else if (format === "pdf") {
        // Convert to PDF
        const doc = new jsPDF();

        // Add title
        doc.setFontSize(18);
        doc.text(reportType, 14, 20);
        doc.setFontSize(12);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);

        const startY = 35;

        if (activeTab === "import") {
          // Import History PDF
          const tableData = data.map((row: any) => [
            row.Date ?? "",
            row["File Name"] ?? "",
            row["Total Rows"] ?? "",
            row.Success ?? "",
            row.Errors ?? "",
            row.Status ?? "",
          ]);

          autoTable(doc, {
            head: [
              [
                "Date",
                "File Name",
                "Total Rows",
                "Success",
                "Errors",
                "Status",
              ],
            ],
            body: tableData,
            startY: startY,
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: {
              fillColor: [74, 82, 115],
              textColor: [255, 255, 255],
              fontStyle: "bold",
            },
            columnStyles: {
              0: { cellWidth: 30 },
              1: { cellWidth: 60 },
              2: { cellWidth: 25 },
              3: { cellWidth: 20 },
              4: { cellWidth: 20 },
              5: { cellWidth: 30 },
            },
            margin: { top: startY },
          });
        } else if (activeTab === "monthly") {
          // Monthly Summary PDF
          const tableData = data.map((row: any) => [
            row["Metric"] ?? "",
            row["Value"] ?? "",
          ]);

          autoTable(doc, {
            head: [["Metric", "Value"]],
            body: tableData,
            startY: startY,
            styles: { fontSize: 12 },
            headStyles: {
              fillColor: [74, 82, 115],
              textColor: [255, 255, 255],
              fontStyle: "bold",
            },
            margin: { top: startY },
          });
        } else if (activeTab === "midr") {
          // MIDR Report PDF
          // Separate data rows from summary rows
          const dataRows = data.filter(
            (row: any) =>
              row["Case No"] &&
              row["Case No"] !== "Total Properties" &&
              row["Case No"] !== "Total Profit Potential" &&
              row["Case No"] !== "Total Investment" &&
              row["Case No"] !== "Average Investment"
          );
          const summaryRows = data.filter(
            (row: any) =>
              !row["Case No"] ||
              row["Case No"] === "Total Properties" ||
              row["Case No"] === "Total Profit Potential" ||
              row["Case No"] === "Total Investment" ||
              row["Case No"] === "Average Investment"
          );

          const tableData = dataRows.map((row: any) => [
            row["Case No"] ?? "",
            row["Parcel ID (APN etc)"] ?? "",
            row["Date"] ?? "",
            typeof row["Lien Amount"] === "number"
              ? `$${row["Lien Amount"].toLocaleString()}`
              : (row["Lien Amount"] ?? ""),
            row["Bed"] ?? "",
            row["Bath"] ?? "",
            row["Sq. Footage"] ?? "",
            row["Final ARV"] === "--"
              ? "--"
              : typeof row["Final ARV"] === "number"
                ? `$${row["Final ARV"].toLocaleString()}`
                : (row["Final ARV"] ?? ""),
            row["ROI"] ?? "",
            typeof row["Profit Potential"] === "number"
              ? `$${row["Profit Potential"].toLocaleString()}`
              : (row["Profit Potential"] ?? ""),
          ]);

          autoTable(doc, {
            head: [
              [
                "Case No",
                "Parcel ID (APN etc)",
                "Date",
                "Lien Amount",
                "Bed",
                "Bath",
                "Sq. Footage",
                "Final ARV",
                "ROI",
                "Profit Potential",
              ],
            ],
            body: tableData,
            startY: startY,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: {
              fillColor: [74, 82, 115],
              textColor: [255, 255, 255],
              fontStyle: "bold",
            },
            margin: { top: startY },
          });

          // Add summary section
          if (summaryRows.length > 0) {
            const lastY = (doc as any).lastAutoTable?.finalY ?? startY + 50;
            doc.setFontSize(14);
            doc.text("Summary", 14, lastY + 15);

            const summaryData = summaryRows
              .filter((row: any) => row["Case No"])
              .map((row: any) => [
                row["Case No"] ?? "",
                row["Parcel ID (APN etc)"] ?? "",
              ]);

            if (summaryData.length > 0) {
              autoTable(doc, {
                head: [["Metric", "Value"]],
                body: summaryData,
                startY: lastY + 20,
                styles: { fontSize: 10 },
                headStyles: {
                  fillColor: [74, 82, 115],
                  textColor: [255, 255, 255],
                  fontStyle: "bold",
                },
              });
            }
          }
        }

        // Save PDF
        doc.save(`${fileName}.pdf`);
        toast.success(`Successfully exported ${reportType} as PDF`);
      }
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        `Failed to export ${reportType}: ${error?.message ?? "Unknown error"}`
      );
    }
  };

  return (
    <Layout>
      <div className="space-y-4 pb-8 lg:space-y-8">
        {/* Modern Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#4a5273] via-[#4a5273] to-[#5a6282] p-8 text-white shadow-xl">
          <div className="relative z-10">
            <h1 className="mb-2 text-2xl font-bold lg:text-4xl">
              Reports Dashboard
            </h1>
            <p className="text-lg text-white/90">
              Comprehensive analytics and insights for your investment portfolio
            </p>
          </div>
          <div className="absolute right-0 top-0 -mr-32 -mt-32 h-64 w-64 rounded-full bg-white/10" />
          <div className="absolute bottom-0 left-0 -mb-24 -ml-24 h-48 w-48 rounded-full bg-white/5" />
        </div>

        <Tabs
          defaultValue="import"
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-8"
        >
          <TabsList className="grid h-auto w-full grid-cols-3 rounded-xl bg-muted/50 p-1 backdrop-blur-sm">
            <TabsTrigger
              value="import"
              className="rounded-lg py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4a5273] data-[state=active]:to-[#4a5273] data-[state=active]:text-white"
            >
              <FileCheck className="mr-2 h-4 w-4" />
              <span className="font-semibold">Import Summary</span>
            </TabsTrigger>
            <TabsTrigger
              value="monthly"
              className="rounded-lg py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4a5273] data-[state=active]:to-[#4a5273] data-[state=active]:text-white"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              <span className="font-semibold">Monthly Summary</span>
            </TabsTrigger>
            <TabsTrigger
              value="midr"
              className="rounded-lg py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4a5273] data-[state=active]:to-[#4a5273] data-[state=active]:text-white"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              <span className="font-semibold">MIDR Report</span>
            </TabsTrigger>
          </TabsList>

          {/* Import Summary Report */}
          <TabsContent value="import" className="animate-fade-in space-y-6">
            <Card className="overflow-hidden border-none shadow-2xl">
              <div className="border-b bg-gradient-to-r from-[#4a5273]/10 to-[#4a5273]/10 px-6 py-5">
                <div className="flex flex-col justify-between gap-4 lg:flex-row">
                  <div>
                    <h2 className="flex items-center gap-2 text-xl font-bold text-foreground lg:text-2xl">
                      <FileCheck className="h-6 w-6 text-[#4a5273]" />
                      Import Summary Report
                    </h2>
                    <p className="text-md mt-1 text-muted-foreground">
                      Overview of all data imports and processing status
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Select
                      value={selectedFormat}
                      onValueChange={setSelectedFormat}
                    >
                      <SelectTrigger className="w-36 border-2 transition-colors hover:border-[#4a5273]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">📊 CSV</SelectItem>
                        <SelectItem value="excel">📈 Excel</SelectItem>
                        <SelectItem value="pdf">📄 PDF</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() =>
                        handleExport("Import Summary", selectedFormat)
                      }
                      className="bg-[#4a5273] shadow-lg transition-all hover:bg-[#4a5273]/90 hover:shadow-xl"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export Report
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="space-y-6 p-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 transition-all hover:border-slate-300 hover:shadow-md">
                    <div className="absolute right-0 top-0 -mr-10 -mt-10 h-20 w-20 rounded-full bg-slate-200/30" />
                    <div className="relative">
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Total Imports
                      </p>
                      <p className="mt-2 text-2xl font-bold text-[#4a5273] lg:text-4xl">
                        {importSummaryData.totalImports}
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-[#4a5273]">
                        <TrendingUp className="h-3 w-3" />
                        <span>Active</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 transition-all hover:border-slate-300 hover:shadow-md">
                    <div className="absolute right-0 top-0 -mr-10 -mt-10 h-20 w-20 rounded-full bg-slate-200/30" />
                    <div className="relative">
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Total Rows
                      </p>
                      <p className="mt-2 text-2xl font-bold text-[#4a5273] lg:text-4xl">
                        {importSummaryData.totalRows}
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-[#4a5273]">
                        <FileSpreadsheet className="h-3 w-3" />
                        <span>Processed</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 transition-all hover:border-slate-300 hover:shadow-md">
                    <div className="absolute right-0 top-0 -mr-10 -mt-10 h-20 w-20 rounded-full bg-slate-200/30" />
                    <div className="relative">
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Success Rate
                      </p>
                      <p className="mt-2 text-2xl font-bold text-slate-700 lg:text-4xl">
                        {Math.round(
                          (importSummaryData.successfulRows /
                            importSummaryData.totalRows) *
                            100
                        )}
                        %
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-slate-700">
                        <CheckCircle2 className="h-3 w-3" />
                        <span>Excellent</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                          Successful Rows
                        </p>
                        <p className="mt-2 text-xl font-bold text-slate-800 lg:text-3xl">
                          {importSummaryData.successfulRows}
                        </p>
                        <p className="mt-1 text-xs text-slate-600">
                          of {importSummaryData.totalRows} total
                        </p>
                      </div>
                      <div className="rounded-xl bg-slate-200/50 p-3">
                        <CheckCircle2 className="h-8 w-8 text-slate-700" />
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                          Error Rows
                        </p>
                        <p className="mt-2 text-xl font-bold text-slate-800 lg:text-3xl">
                          {importSummaryData.errorRows}
                        </p>
                        <p className="mt-1 text-xs text-slate-600">
                          requires attention
                        </p>
                      </div>
                      <div className="rounded-xl bg-slate-200/50 p-3">
                        <AlertCircle className="h-8 w-8 text-slate-700" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-6">
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-foreground">
                    <Calendar className="h-4 w-4 text-[#4a5273]" />
                    Import Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b py-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Last Import Date
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {importSummaryData.lastImport}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Mapping Used
                      </span>
                      <Badge variant="outline" className="font-semibold">
                        {importSummaryData.mappingUsed}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-lg font-bold lg:text-xl">
                      <FileText className="h-5 w-5 text-[#4a5273]" />
                      Import History
                    </h3>
                    <Select
                      value={importHistoryFilter}
                      onValueChange={(
                        value: "all" | "completed" | "rejected"
                      ) => setImportHistoryFilter(value)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-md">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="font-bold">Date</TableHead>
                          <TableHead className="font-bold">File Name</TableHead>
                          <TableHead className="font-bold">
                            Total Rows
                          </TableHead>
                          <TableHead className="font-bold">Success</TableHead>
                          <TableHead className="font-bold">Errors</TableHead>
                          <TableHead className="font-bold">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          // Filter import details based on selected filter
                          const filteredDetails = importDetails.filter(
                            (item: any) => {
                              if (importHistoryFilter === "all") {
                                return true;
                              }
                              const statusLower = (
                                item.status ?? ""
                              ).toLowerCase();
                              if (importHistoryFilter === "completed") {
                                return (
                                  statusLower === "completed" ||
                                  statusLower.includes("completed")
                                );
                              }
                              if (importHistoryFilter === "rejected") {
                                return (
                                  statusLower === "rejected" ||
                                  statusLower === "failed"
                                );
                              }
                              return true;
                            }
                          );

                          if (filteredDetails.length === 0) {
                            return (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  className="py-8 text-center text-muted-foreground"
                                >
                                  No import history found
                                  {importHistoryFilter !== "all" &&
                                    ` for ${importHistoryFilter} status`}
                                </TableCell>
                              </TableRow>
                            );
                          }

                          return filteredDetails.map((item: any) => {
                            // Helper to format date
                            const formatDate = (dateStr: string) => {
                              if (!dateStr) {
                                return "-";
                              }
                              try {
                                const date = new Date(dateStr);
                                if (isNaN(date.getTime())) {
                                  return dateStr;
                                }
                                return date
                                  .toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  })
                                  .replace(/\//g, "-");
                              } catch {
                                return dateStr;
                              }
                            };

                            const getStatusBadge = (status: string) => {
                              const statusLower = status.toLowerCase();
                              if (statusLower === "completed") {
                                return (
                                  <Badge className="bg-slate-700 text-white hover:bg-slate-800">
                                    {status}
                                  </Badge>
                                );
                              } else if (statusLower.includes("error")) {
                                return (
                                  <Badge className="bg-orange-600 text-white hover:bg-orange-700">
                                    {status}
                                  </Badge>
                                );
                              } else if (
                                statusLower === "failed" ||
                                statusLower === "rejected"
                              ) {
                                return (
                                  <Badge className="bg-red-600 text-white hover:bg-red-700">
                                    {status}
                                  </Badge>
                                );
                              }
                              return (
                                <Badge
                                  variant="outline"
                                  className="font-semibold"
                                >
                                  {status}
                                </Badge>
                              );
                            };

                            return (
                              <TableRow
                                key={`${item.date}-${item.fileName}`}
                                className="transition-colors hover:bg-muted/30"
                              >
                                <TableCell className="font-medium">
                                  {formatDate(item.date)}
                                </TableCell>
                                <TableCell>
                                  <span className="cursor-pointer font-medium text-blue-600 hover:text-blue-800">
                                    {item.fileName}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className="bg-muted/50 font-semibold"
                                  >
                                    {item.rows}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    <span className="font-semibold">
                                      {item.success}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <XCircle className="h-4 w-4 text-yellow-600" />
                                    <span className="font-semibold">
                                      {item.errors}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {getStatusBadge(item.status)}
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Property Summary */}
          <TabsContent value="monthly" className="animate-fade-in space-y-6">
            <Card className="overflow-hidden border-none shadow-2xl">
              <div className="border-b bg-gradient-to-r from-[#4a5273]/10 to-[#4a5273]/10 px-6 py-5">
                <div className="flex flex-col justify-between gap-4 lg:flex-row">
                  <div>
                    <h2 className="flex items-center gap-2 text-xl font-bold text-foreground lg:text-2xl">
                      <TrendingUp className="h-6 w-6 text-[#4a5273]" />
                      Monthly Property Summary
                    </h2>
                    <p className="text-md mt-1 text-muted-foreground">
                      Dashboard report with KPIs and analytics
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Select
                      value={selectedFormat}
                      onValueChange={setSelectedFormat}
                    >
                      <SelectTrigger className="w-36 border-2 transition-colors hover:border-[#4a5273]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">📊 CSV</SelectItem>
                        <SelectItem value="excel">📈 Excel</SelectItem>
                        <SelectItem value="pdf">📄 PDF</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() =>
                        handleExport("Monthly Summary", selectedFormat)
                      }
                      className="bg-[#4a5273] shadow-lg transition-all hover:bg-[#4a5273]/90 hover:shadow-xl"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export Report
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="space-y-8 p-6">
                {propertiesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <p className="text-muted-foreground">
                      Loading property data...
                    </p>
                  </div>
                ) : properties.length === 0 ? (
                  <div className="rounded-xl border border-border bg-muted/30 p-8 text-center">
                    <p className="text-muted-foreground">
                      No property data available. Start adding properties to see
                      statistics here.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
                      {finalMonthlyKPIs.map(kpi => {
                        const Icon = kpi.icon;
                        return (
                          <div
                            key={kpi.label}
                            className="group relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 transition-all hover:shadow-md"
                          >
                            <div className="absolute right-0 top-0 -mr-10 -mt-10 h-20 w-20 rounded-full bg-slate-200/30 transition-transform group-hover:scale-150" />
                            <div className="relative">
                              <div className="mb-3 flex items-start justify-between">
                                <Icon className="h-8 w-8 text-[#4a5273]" />
                                <Badge
                                  variant="outline"
                                  className="border-slate-300 bg-white text-xs font-bold"
                                >
                                  {kpi.change}
                                </Badge>
                              </div>
                              <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                                {kpi.label}
                              </p>
                              <p className="mt-2 text-xl font-bold text-foreground lg:text-3xl">
                                {kpi.value}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-md">
                        <div className="border-b bg-muted/30 px-6 py-4">
                          <h3 className="flex items-center gap-2 text-lg font-bold">
                            <TrendingUp className="h-5 w-5 text-[#17a2b8]" />
                            Monthly Property Growth
                          </h3>
                        </div>
                        <div className="p-6">
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={finalMonthlyChartData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="hsl(var(--border))"
                              />
                              <XAxis
                                dataKey="month"
                                stroke="hsl(var(--muted-foreground))"
                                style={{ fontSize: "12px", fontWeight: 600 }}
                              />
                              <YAxis
                                stroke="hsl(var(--muted-foreground))"
                                style={{ fontSize: "12px", fontWeight: 600 }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "hsl(var(--card))",
                                  border: "1px solid hsl(var(--border))",
                                  borderRadius: "8px",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                }}
                              />
                              <Legend
                                wrapperStyle={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                }}
                              />
                              <Bar
                                dataKey="properties"
                                fill="#17a2b8"
                                name="Properties"
                                radius={[6, 6, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-md">
                        <div className="border-b bg-muted/30 px-6 py-4">
                          <h3 className="flex items-center gap-2 text-lg font-bold">
                            <Home className="h-5 w-5 text-[#17a2b8]" />
                            Property Status Distribution
                          </h3>
                        </div>
                        <div className="p-6">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={finalStatusPieData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) =>
                                  `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                                }
                                outerRadius={90}
                                fill="#8884d8"
                                dataKey="value"
                                strokeWidth={2}
                                stroke="hsl(var(--card))"
                              >
                                {finalStatusPieData.map(entry => (
                                  <Cell key={entry.name} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="mt-6 flex justify-center gap-6">
                            {finalStatusPieData.map(item => (
                              <div
                                key={item.name}
                                className="flex items-center gap-2"
                              >
                                <div
                                  className="h-3 w-3 rounded-full"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span className="text-sm font-semibold">
                                  {item.name}: {item.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Master Investment Decision Report */}
          <TabsContent value="midr" className="animate-fade-in space-y-6">
            <Card className="overflow-hidden border-none shadow-2xl">
              <div className="border-b bg-gradient-to-r from-[#0ea5b9]/10 to-[#17a2b8]/10 px-6 py-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="flex items-center gap-2 text-xl font-bold text-foreground lg:text-2xl">
                      <FileSpreadsheet className="h-6 w-6 text-[#17a2b8]" />
                      Master Investment Decision Report (MIDR)
                    </h2>
                    <p className="text-md mt-1 text-muted-foreground">
                      Consolidated property analysis with ARV, Rehab, and Max
                      Bid calculations
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Select
                      value={selectedFormat}
                      onValueChange={setSelectedFormat}
                    >
                      <SelectTrigger className="w-36 border-2 transition-colors hover:border-[#17a2b8]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">📊 CSV</SelectItem>
                        <SelectItem value="excel">📈 Excel</SelectItem>
                        <SelectItem value="pdf">📄 PDF</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() =>
                        handleExport("MIDR Report", selectedFormat)
                      }
                      className="bg-[#4a5273] shadow-lg transition-all hover:bg-[#4a5273]/90 hover:shadow-xl"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export Report
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="space-y-8 p-3 lg:p-6">
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-md">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#4a5273] hover:bg-[#4a5273]/90">
                          {midrTableColumns.map(
                            (column: { key: string; label: string }) => (
                              <TableHead
                                key={column.key}
                                className="whitespace-nowrap font-bold text-white"
                              >
                                {column.label}
                              </TableHead>
                            )
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {propertiesLoading ? (
                          <TableRow>
                            <TableCell
                              colSpan={midrTableColumns.length}
                              className="py-8 text-center"
                            >
                              Loading property data...
                            </TableCell>
                          </TableRow>
                        ) : finalMidrData.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={midrTableColumns.length}
                              className="py-8 text-center"
                            >
                              No property data available
                            </TableCell>
                          </TableRow>
                        ) : (
                          finalMidrData.map((property: any, index: number) => {
                            // Helper to get field value (case-insensitive)
                            const getValue = (fieldKey: string): any => {
                              // First try normalized lowercase
                              const normalized: any = {};
                              Object.keys(property ?? {}).forEach(k => {
                                normalized[k.toLowerCase()] = property[k];
                              });

                              // Try direct match first
                              if (property[fieldKey] !== undefined) {
                                return property[fieldKey];
                              }

                              // Try normalized
                              if (
                                normalized[fieldKey.toLowerCase()] !== undefined
                              ) {
                                return normalized[fieldKey.toLowerCase()];
                              }

                              // Try case variations
                              const keys = Object.keys(property ?? {});
                              const matchingKey = keys.find(
                                k => k.toLowerCase() === fieldKey.toLowerCase()
                              );
                              return matchingKey ? property[matchingKey] : null;
                            };

                            // Format value for display
                            const formatValue = (
                              value: any,
                              fieldKey: string
                            ): string | React.ReactNode => {
                              if (
                                value === null ||
                                value === undefined ||
                                value === ""
                              ) {
                                return "--";
                              }

                              // Special formatting for specific fields
                              if (fieldKey === "roi") {
                                return (
                                  <span className="inline-flex items-center gap-1 font-bold text-slate-700">
                                    <TrendingUp className="h-4 w-4" />
                                    {typeof value === "string"
                                      ? value
                                      : `${value}%`}
                                  </span>
                                );
                              }

                              if (fieldKey === "profitPotential") {
                                const num =
                                  typeof value === "number"
                                    ? value
                                    : (() => {
                                        const n = Number(value);
                                        return isNaN(n) ? 0 : n;
                                      })();
                                return (
                                  <span className="font-bold text-slate-700">
                                    ${num.toLocaleString()}
                                  </span>
                                );
                              }

                              // Format currency fields
                              const currencyFields = [
                                "lienamount",
                                "lien",
                                "minbid",
                                "minbid",
                                "startingbid",
                                "rougharv",
                                "finalarv",
                                "arv",
                                "rehabestimate",
                                "rehabcost",
                                "maxbid",
                                "maxbid",
                                "profitpotential",
                                "profit",
                              ];
                              if (
                                currencyFields.some(f =>
                                  fieldKey.toLowerCase().includes(f)
                                )
                              ) {
                                const num =
                                  typeof value === "number"
                                    ? value
                                    : (() => {
                                        const n = Number(value);
                                        return isNaN(n) ? 0 : n;
                                      })();
                                return `$${num.toLocaleString()}`;
                              }

                              // Format bed and bath as separate fields (show all Property Master fields)
                              // Both will be displayed as separate columns

                              // Format status
                              if (fieldKey.toLowerCase() === "status") {
                                const status = String(value);
                                return (
                                  <Badge
                                    className={
                                      status === "Active"
                                        ? "bg-slate-700 hover:bg-slate-800"
                                        : status === "Pending"
                                          ? "bg-slate-500 hover:bg-slate-600"
                                          : "bg-slate-600 hover:bg-slate-700"
                                    }
                                  >
                                    {status}
                                  </Badge>
                                );
                              }

                              // Format rehab type
                              if (
                                fieldKey.toLowerCase().includes("rehabtype") ||
                                fieldKey.toLowerCase().includes("rehab_type")
                              ) {
                                return (
                                  <Badge
                                    variant="outline"
                                    className="font-semibold"
                                  >
                                    {String(value)}
                                  </Badge>
                                );
                              }

                              // Format numbers
                              if (typeof value === "number") {
                                return value.toLocaleString();
                              }

                              // Format arrays/objects
                              if (Array.isArray(value)) {
                                return value.length > 0
                                  ? `${value.length} item(s)`
                                  : "--";
                              }

                              if (typeof value === "object" && value !== null) {
                                return JSON.stringify(value);
                              }

                              return String(value);
                            };

                            return (
                              <TableRow
                                key={property.id ?? index}
                                className={
                                  index % 2 === 0 ? "bg-muted/30" : "bg-card"
                                }
                              >
                                {midrTableColumns.map(
                                  (column: { key: string; label: string }) => {
                                    const value = getValue(column.key);
                                    const formatted = formatValue(
                                      value,
                                      column.key
                                    );

                                    // Skip rendering if null (e.g., bath when bed/bath is combined)
                                    if (formatted === null) {
                                      return (
                                        <TableCell key={column.key}>
                                          --
                                        </TableCell>
                                      );
                                    }

                                    // Special styling for key fields
                                    const isKeyField = [
                                      "caseno",
                                      "case no",
                                      "propertyname",
                                      "property name",
                                      "parcelid",
                                      "parcel id",
                                      "maxbid",
                                      "max bid",
                                    ].some(f =>
                                      column.key.toLowerCase().includes(f)
                                    );

                                    const isParcelId =
                                      column.key.toLowerCase() === "parcelid" ||
                                      column.key.toLowerCase() === "parcel id";

                                    return (
                                      <TableCell
                                        key={column.key}
                                        className={
                                          isKeyField
                                            ? "font-semibold text-[#4a5273]"
                                            : isParcelId
                                              ? "rounded bg-muted px-2 py-1 font-mono text-xs"
                                              : ""
                                        }
                                      >
                                        {formatted}
                                      </TableCell>
                                    );
                                  }
                                )}
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                  <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-md transition-all hover:shadow-lg">
                    <div className="absolute right-0 top-0 -mr-12 -mt-12 h-24 w-24 rounded-full bg-slate-200/30 transition-transform group-hover:scale-150" />
                    <div className="relative">
                      <DollarSign className="mb-3 h-8 w-8 text-[#4a5273]" />
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Total Properties
                      </p>
                      <p className="mt-2 text-2xl font-bold text-foreground lg:text-4xl">
                        {finalMidrData.length}
                      </p>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-md transition-all hover:shadow-lg">
                    <div className="absolute right-0 top-0 -mr-12 -mt-12 h-24 w-24 rounded-full bg-slate-200/30 transition-transform group-hover:scale-150" />
                    <div className="relative">
                      <DollarSign className="mb-3 h-8 w-8 text-slate-700" />
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Total Profit Potential
                      </p>
                      <p className="mt-2 text-2xl font-bold text-foreground lg:text-4xl">
                        {midrSummaryMetrics.totalProfitPotential >= 1000000
                          ? `$${(midrSummaryMetrics.totalProfitPotential / 1000000).toFixed(1)}M`
                          : `$${(midrSummaryMetrics.totalProfitPotential / 1000).toFixed(0)}K`}
                      </p>
                    </div>
                  </div>
                  <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-md transition-all hover:shadow-lg">
                    <div className="absolute right-0 top-0 -mr-12 -mt-12 h-24 w-24 rounded-full bg-slate-200/30 transition-transform group-hover:scale-150" />
                    <div className="relative">
                      <Home className="mb-3 h-8 w-8 text-slate-600" />
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Total Investment
                      </p>
                      <p className="mt-2 text-2xl font-bold text-foreground lg:text-4xl">
                        {midrSummaryMetrics.totalInvestment >= 1000000
                          ? `$${(midrSummaryMetrics.totalInvestment / 1000000).toFixed(1)}M`
                          : `$${(midrSummaryMetrics.totalInvestment / 1000).toFixed(0)}K`}
                      </p>
                    </div>
                  </div>
                  <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-md transition-all hover:shadow-lg">
                    <div className="absolute right-0 top-0 -mr-12 -mt-12 h-24 w-24 rounded-full bg-slate-200/30 transition-transform group-hover:scale-150" />
                    <div className="relative">
                      <TrendingUp className="mb-3 h-8 w-8 text-slate-600" />
                      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Average Investment
                      </p>
                      <p className="mt-2 text-2xl font-bold text-foreground lg:text-4xl">
                        {midrSummaryMetrics.averageInvestment >= 1000000
                          ? `$${(midrSummaryMetrics.averageInvestment / 1000000).toFixed(1)}M`
                          : `$${(midrSummaryMetrics.averageInvestment / 1000).toFixed(0)}K`}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default PP_Reports;
